import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, Button, Typography, Paper, MenuItem, Select, FormControl, Alert, CircularProgress, Autocomplete } from '@mui/material';
import { Description as DescriptionIcon, Language as LanguageIcon, People as PeopleIcon, LocationOn as LocationOnIcon, CalendarToday as CalendarIcon, CloudUpload as UploadIcon, } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SignUpImage from '../../Assets/Images/artifical_intelligence.png';
import { companyDetailsApi } from '../../Api/CompanyApi';
import { getIndustryOptions } from '../../Api/IndustryOptionsapi';

const CompanyDetails = ({ onlyForm }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        companyName: '',
        gstinNumber: '',
        websiteLink: '',
        industryType: '',
        companySize: '',
        location: '',
        foundedIn: '',
        logo: null,
    });

    const [industryOptions, setIndustryOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingIndustries, setLoadingIndustries] = useState(true);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({});
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const locationDebounceRef = useRef(null);

    // Fetch industry options on component mount
    useEffect(() => {
        const fetchIndustryOptions = async () => {
            try {
                setLoadingIndustries(true);


                const token = localStorage.getItem('token') || localStorage.getItem('authToken');

                // Redirect if not registered/authenticated
                if (!token) {
                    navigate('/signup');
                    return;
                }

                const employerId = localStorage.getItem('employerId');

                const response = await getIndustryOptions(employerId);
                // Assuming the API returns an array of industry objects with id and name properties
                // If the structure is different, adjust accordingly
                setIndustryOptions(response.data || response || []);
            } catch (error) {
                console.error('Error fetching industry options:', error);
                // Don't display alert message - just log the error
            } finally {
                setLoadingIndustries(false);
            }
        };

        fetchIndustryOptions();
    }, []);

    const handleInputChange = (field) => (e) => {
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
        setError(''); // Clear general error when user starts typing
    };

    const handleWebsiteChange = (e) => {
        const val = e.target.value;
        setFormData((prev) => ({ ...prev, websiteLink: val }));

        let formattedVal = val.trim();
        if (formattedVal && !formattedVal.match(/^https?:\/\//i)) {
            formattedVal = `https://${formattedVal}`;
        }

        if (!val.trim()) {
            setErrors((prev) => ({ ...prev, websiteLink: 'Please enter your website link' }));
        } else if (!formattedVal.match(/^https?:\/\/[a-zA-Z0-9-+&@#/%?=~_|!:,.;]*\.[a-zA-Z]{2,}(?:\/.*)?$/)) {
            setErrors((prev) => ({ ...prev, websiteLink: 'Please enter a valid website URL (e.g., https://www.example.com)' }));
        } else {
            setErrors((prev) => ({ ...prev, websiteLink: '' }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({
                ...prev,
                logo: file,
            }));
        }
        setError(''); // Clear error when file is selected
    };

    const fetchLocationSuggestions = useCallback((query) => {
        if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
        if (!query || query.length < 2) {
            setLocationSuggestions([]);
            return;
        }
        locationDebounceRef.current = setTimeout(async () => {
            setLocationLoading(true);
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=7&q=${encodeURIComponent(query)}`,
                    { headers: { 'Accept-Language': 'en' } }
                );
                const data = await res.json();
                const suggestions = data.map((item) => item.display_name);
                setLocationSuggestions(suggestions);
            } catch {
                setLocationSuggestions([]);
            } finally {
                setLocationLoading(false);
            }
        }, 400);
    }, []);

    const validateForm = () => {
        let newErrors = {};
        let isValid = true;

        const companyName = String(formData.companyName || '').trim();
        if (!companyName) {
            newErrors.companyName = 'Please enter your company name';
            isValid = false;
        }

        const gstinNumber = String(formData.gstinNumber || '').trim();
        if (gstinNumber) {
            const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
            if (!gstinRegex.test(gstinNumber)) {
                newErrors.gstinNumber = 'Please enter a valid 15-character GSTIN';
                isValid = false;
            }
        }

        let websiteLink = String(formData.websiteLink || '').trim();
        if (websiteLink && !websiteLink.match(/^https?:\/\//i)) {
            websiteLink = `https://${websiteLink}`;
            setFormData(prev => ({ ...prev, websiteLink: websiteLink }));
        }

        if (!websiteLink) {
            newErrors.websiteLink = 'Please enter your website link';
            isValid = false;
        } else if (!websiteLink.match(/^https?:\/\/[a-zA-Z0-9-+&@#/%?=~_|!:,.;]*\.[a-zA-Z]{2,}(?:\/.*)?$/)) {
            newErrors.websiteLink = 'Please enter a valid website URL (e.g., https://www.example.com)';
            isValid = false;
        }

        if (!formData.industryType) {
            newErrors.industryType = 'Please select an industry type';
            isValid = false;
        }

        const companySize = String(formData.companySize || '').trim();
        if (!companySize) {
            newErrors.companySize = 'Please enter company size';
            isValid = false;
        }

        const location = String(formData.location || '').trim();
        if (!location) {
            newErrors.location = 'Please enter company location';
            isValid = false;
        }

        const foundedIn = String(formData.foundedIn || '').trim();
        if (!foundedIn) {
            newErrors.foundedIn = 'Please enter the year the company was founded';
            isValid = false;
        } else {
            const year = parseInt(foundedIn);
            const currentYear = new Date().getFullYear();
            if (isNaN(year) || year < 1800 || year > currentYear) {
                newErrors.foundedIn = 'Please enter a valid year';
                isValid = false;
            }
        }

        if (!formData.logo) {
            newErrors.logo = 'Please upload your company logo';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // Validate form
        if (!validateForm()) {
            return;
        }
        setLoading(true);

        try {
            // Get industryId from the selected industry option
            const selectedIndustry = industryOptions.find(
                (industry) => industry.id === formData.industryType || industry.name === formData.industryType
            );
            const industryId = selectedIndustry?.id?.toString() || formData.industryType;

            // Get employerId from localStorage
            const employerId = localStorage.getItem('employerId');

            const payload = {
                name: formData.companyName.trim(),
                gstin: formData.gstinNumber.trim() || '',
                website: formData.websiteLink.trim(),
                industryId: industryId,
                companySize: formData.companySize.trim(),
                locationText: formData.location.trim(),
                foundedYear: formData.foundedIn.trim(),
                employerId: employerId,
                logo: formData.logo // Added logo upload field
            };

            const response = await companyDetailsApi(payload);

            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('hasCompany', 'true');
            // Use hard redirect so ProtectedRoutes re-reads updated localStorage
            window.location.href = '/dashboard';
        } catch (error) {
            console.error('Error response:', error.response);
            const data = error.response?.data;
            if (data && data.errors && Array.isArray(data.errors)) {
                const newErrors = {};
                let hasMappedError = false;
                data.errors.forEach(err => {
                    const lowercaseErr = err.toLowerCase();
                    if (lowercaseErr.includes('website')) {
                        newErrors.websiteLink = 'Invalid website URL format';
                        hasMappedError = true;
                    }
                    if (lowercaseErr.includes('gstin')) {
                        newErrors.gstinNumber = err;
                        hasMappedError = true;
                    }
                    if (lowercaseErr.includes('name')) {
                        newErrors.companyName = err;
                        hasMappedError = true;
                    }
                    if (lowercaseErr.includes('companysize')) {
                        newErrors.companySize = err;
                        hasMappedError = true;
                    }
                    if (lowercaseErr.includes('location')) {
                        newErrors.location = err;
                        hasMappedError = true;
                    }
                    if (lowercaseErr.includes('foundedyear')) {
                        newErrors.foundedIn = err;
                        hasMappedError = true;
                    }
                });
                if (hasMappedError) {
                    setErrors(newErrors);
                    setError('Please correct the highlighted fields.');
                } else {
                    setError(data.message || data.errors.join(', ') || 'Failed to save company details.');
                }
            } else if (data && data.message) {
                if (data.message.toLowerCase().includes('gstin')) {
                    setErrors({ gstinNumber: data.message });
                    setError('Please correct the highlighted fields.');
                } else {
                    setError(data.message);
                }
            } else {
                setError(error.message || 'Failed to save company details. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const content = (
        <Box
            sx={{
                width: onlyForm ? '100%' : { xs: '100%', md: '55%' },
                marginLeft: onlyForm ? 0 : { xs: 0, md: '45%' },
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: { xs: '24px 0px', md: 4 },
                position: 'relative',
                minHeight: '100vh',
                boxSizing: 'border-box',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
            }}
        >
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 600,
                        padding: { xs: 3, md: 4 },
                        borderRadius: '16px',
                    }}
                >
                    {/* Step progress bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#10B981', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                                ✓
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#10B981' }}>Basic Details</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1, height: '2px', backgroundColor: '#5F27CD', mx: 2 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#5F27CD', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                                2
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5F27CD' }}>Company Setup</Typography>
                        </Box>
                    </Box>

                    {/* Title */}
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#000', mb: 1, fontSize: { xs: '24px', md: '32px' } }}>
                        Company Setup
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 4, fontWeight: 500 }}>
                        Step 2 of 2 • Company Details
                    </Typography>

                    {/* Error Message */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}> {error} </Alert>
                    )}

                    {/* Section 1: Company Information */}
                    <Box sx={{ mb: 4, p: 2.5, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#5F27CD', mb: 2.5, borderBottom: '1px solid #f3f4f6', pb: 1 }}>
                            Company Information
                        </Typography>

                        {/* Company Name Field */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DescriptionIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }}   >
                                    Company Name <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <TextField fullWidth size="small" placeholder="Enter company Name"
                                value={formData.companyName} onChange={handleInputChange('companyName')}
                                disabled={loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' }, }}
                                error={!!errors.companyName} helperText={errors.companyName}
                            />
                        </Box>

                        {/* Industry Type Field */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DescriptionIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }}   >
                                    Industry Type <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <FormControl fullWidth size="small" error={!!errors.industryType}>
                                <Select
                                    value={formData.industryType} onChange={handleInputChange('industryType')}
                                    displayEmpty disabled={loading || loadingIndustries}
                                    sx={{
                                        borderRadius: '4px', '& .MuiSelect-icon': { color: '#000', },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        {loadingIndustries ? 'Loading industries...' : 'Select Your Industry'}
                                    </MenuItem>
                                    {industryOptions.map((industry) => (
                                        <MenuItem key={industry.id || industry.name} value={industry.id || industry.name}>
                                            {industry.name || industry}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.industryType && <Typography variant="caption" color="error" sx={{ mx: 1.5, mt: 0.5 }}>{errors.industryType}</Typography>}
                            </FormControl>
                        </Box>

                        {/* Website Link Field */}
                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LanguageIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }} >
                                    Website Link <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth size="small" placeholder="Enter Company website"
                                value={formData.websiteLink}
                                onChange={handleWebsiteChange}
                                onBlur={(e) => {
                                    let val = e.target.value.trim();
                                    if (val && !val.match(/^https?:\/\//i)) {
                                        val = `https://${val}`;
                                    }
                                    setFormData((prev) => ({
                                        ...prev,
                                        websiteLink: val,
                                    }));

                                    if (!val) {
                                        setErrors((prev) => ({ ...prev, websiteLink: 'Please enter your website link' }));
                                    } else if (!val.match(/^https?:\/\/[a-zA-Z0-9-+&@#/%?=~_|!:,.;]*\.[a-zA-Z]{2,}(?:\/.*)?$/)) {
                                        setErrors((prev) => ({ ...prev, websiteLink: 'Please enter a valid website URL (e.g., https://www.example.com)' }));
                                    } else {
                                        setErrors((prev) => ({ ...prev, websiteLink: '' }));
                                    }
                                }}
                                disabled={loading} type="url"
                                error={!!errors.websiteLink} helperText={errors.websiteLink || 'Example: www.google.com'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Section 2: Organization */}
                    <Box sx={{ mb: 4, p: 2.5, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#5F27CD', mb: 2.5, borderBottom: '1px solid #f3f4f6', pb: 1 }}>
                            Organization Setup
                        </Typography>

                        {/* Company Size Field */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PeopleIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }}   >
                                    Company Size <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <TextField fullWidth size="small" placeholder="Enter Company Size"
                                value={formData.companySize} onChange={handleInputChange('companySize')}
                                disabled={loading}
                                error={!!errors.companySize} helperText={errors.companySize || 'Example: 50'}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px', }, }}
                            />
                        </Box>

                        {/* Founded In Field */}
                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }}    >
                                    Founded In <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth size="small" type="number"
                                placeholder="Enter Company Started Year" value={formData.foundedIn}
                                onChange={handleInputChange('foundedIn')} disabled={loading}
                                error={!!errors.foundedIn} helperText={errors.foundedIn || 'Example: 2015'}
                                inputProps={{ min: 1800, max: new Date().getFullYear() }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '4px', },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Section 3: Location */}
                    <Box sx={{ mb: 4, p: 2.5, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#5F27CD', mb: 2.5, borderBottom: '1px solid #f3f4f6', pb: 1 }}>
                            Location Details
                        </Typography>

                        {/* Location Field */}
                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocationOnIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }}>
                                    Location <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <Autocomplete
                                freeSolo
                                options={locationSuggestions}
                                loading={locationLoading}
                                value={formData.location}
                                disabled={loading}
                                onInputChange={(event, newValue) => {
                                    setFormData((prev) => ({ ...prev, location: newValue }));
                                    setErrors((prev) => ({ ...prev, location: '' }));
                                    fetchLocationSuggestions(newValue);
                                }}
                                onChange={(event, selectedValue) => {
                                    if (selectedValue) {
                                        setFormData((prev) => ({ ...prev, location: selectedValue }));
                                        setErrors((prev) => ({ ...prev, location: '' }));
                                        setLocationSuggestions([]);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        placeholder="Search city, area or address..."
                                        error={!!errors.location}
                                        helperText={errors.location || 'Example: Hyderabad, Telangana, India'}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {locationLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    {/* Section 4: Verification & Branding */}
                    <Box sx={{ mb: 4, p: 2.5, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#5F27CD', mb: 2.5, borderBottom: '1px solid #f3f4f6', pb: 1 }}>
                            Verification & Branding
                        </Typography>

                        {/* GSTIN Number Field */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DescriptionIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, }}  >
                                    GSTIN Number
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth size="small" placeholder="Enter GST Number"
                                value={formData.gstinNumber} onChange={handleInputChange('gstinNumber')}
                                disabled={loading}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px', }, }}
                                error={!!errors.gstinNumber} helperText={errors.gstinNumber || 'Example: 22AAAAA0000A1Z5'}
                            />
                        </Box>

                        {/* Upload Logo Field */}
                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <UploadIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                    Upload Logo <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <Box onClick={() => !loading && document.getElementById('logo-upload').click()}
                                sx={{
                                    border: `2px dashed ${errors.logo ? '#d32f2f' : '#C3C3C3'}`, borderRadius: '4px',
                                    padding: 3, textAlign: 'center', cursor: loading ? 'not-allowed' : 'pointer',
                                    backgroundColor: loading ? '#F0F0F0' : '#FAFAFA', opacity: loading ? 0.6 : 1,
                                    '&:hover': {
                                        backgroundColor: loading ? '#F0F0F0' : '#F5F5F5',
                                        borderColor: loading ? '#C3C3C3' : theme.palette.primary.main,
                                    },
                                }}>
                                <input id="logo-upload" type="file" accept=".pdf,.png,.jpg,.jpeg"
                                    style={{ display: 'none' }} onChange={handleFileChange} disabled={loading}
                                />
                                <UploadIcon sx={{ color: errors.logo ? '#d32f2f' : theme.palette.primary.main, fontSize: 40, mb: 1 }} />
                                <Typography variant="body2" sx={{ color: errors.logo ? '#d32f2f' : '#666', fontSize: '12px' }}>
                                    PDF, PNG, JPG file
                                </Typography>
                                {formData.logo && (
                                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, mt: 1, fontSize: '12px' }}>
                                        {formData.logo.name}
                                    </Typography>
                                )}
                            </Box>
                            {errors.logo && (
                                <Typography variant="caption" color="error" sx={{ mx: 1.5, mt: 0.5, display: 'block' }}>
                                    {errors.logo}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Save & Continue Button */}
                    <Button
                        type="button" disableElevation disableFocusRipple disableRipple disableTouchRipple fullWidth
                        variant="contained" onClick={handleSubmit} disabled={loading}
                        sx={{
                            backgroundColor: '#5F27CD', color: 'white', py: 1.4, borderRadius: '4px', fontWeight: 600,
                            textTransform: 'none', fontSize: '16px',
                            '&:hover': {
                                backgroundColor: '#4A1FA3',
                            },
                            '&:disabled': {
                                backgroundColor: '#9ca3af',
                            },
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                                <span>Saving...</span>
                            </Box>
                        ) : (
                            'Save & Continue'
                        )}
                    </Button>
                </Paper>
        </Box>
    );

    if (onlyForm) {
        return content;
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden',
            }}
        >
            {/* Left Section - Purple Background */}
            <Box
                sx={{
                    width: { xs: '100%', md: '45%' },
                    backgroundColor: '#5F27CD',
                    position: { xs: 'relative', md: 'fixed' },
                    top: { xs: 'auto', md: 0 },
                    left: { xs: 'auto', md: 0 },
                    height: { xs: 'auto', md: '100vh' },
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                    overflow: 'hidden',
                    zIndex: 1,
                }}
            >
                {/* Illustration Image */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                    }}
                >
                    <Box
                        component="img"
                        src={SignUpImage}
                        alt="Company Details"
                        sx={{ objectFit: 'contain', width: 445 }}
                    />
                </Box>

                {/* Tagline Text */}
                <Typography
                    variant="h5"
                    sx={{
                        color: 'white',
                        fontWeight: 400,
                        textAlign: 'center',
                    }}
                >
                    Transforming hiring through smarter sourcing strategies.
                </Typography>
            </Box>

            {content}
        </Box>
    );
};

export default CompanyDetails;

